<template>
    <div id="song-editor">
        <v-navigation-drawer :value="editionMode" fixed right clipped app stateless width="230">
            <div class="title mt-2 mb-2">{{ $t('edit') }}</div>
            <v-divider></v-divider>

            <v-form class="mb-5">
                <div class="pa-2">
                    <v-text-field v-model="title" v-on:blur="updateSongAction({ property: 'title', value: title })"
                                  :label="$t('title')" required clearable></v-text-field>
                    <v-text-field v-model="author" v-on:blur="updateSongAction({ property: 'author', value: author })"
                                  :label="$t('author')" clearable></v-text-field>
                    <v-text-field v-model="album" v-on:blur="updateSongAction({ property: 'album', value: album })"
                                  :label="$t('album')" clearable></v-text-field>
                    <v-select :items="keys" v-model="key" clearable
                              :label="$t('key')" v-on:change="updateSongAction({ property: 'key', value: key })">
                    </v-select>
                    <v-select :items="timeSignaturesList" v-model="timeSignature" :label="$t('timeSignature')"
                              v-on:change="updateSongAction({ property: 'timeSignature', value: timeSignature })"></v-select>
                    <v-text-field v-model="tempo" type="number" v-on:blur="updateSongAction({ property: 'tempo', value: tempo })"
                                  :label="$t('tempo') + ' (en bpm)'" min="0" max="320" step="5"></v-text-field>
                    <v-select :items="capos" v-model="capo" :label="$t('capo')"
                              v-on:change="updateSongAction({ property: 'capo', value: capo })"></v-select>
                    <v-checkbox :label="$t('display-measure-number')" v-model="displayMeasureNumber"
                                v-on:change="updateSongAction({ property: 'displayMeasureNumber', value: displayMeasureNumber })">
                    </v-checkbox>
                </div>

                <div>
                    <v-btn @click="addPartAction()">{{ $t('add-part') }}</v-btn>
                </div>
                
                <div class="mt-3">
                    <v-btn color="green" @click="saveSong()" :disabled="canBeSaved">
                        <v-icon>save</v-icon>&nbsp;
                        {{ $t('save') }}
                    </v-btn>
                </div>
            </v-form>
        </v-navigation-drawer>

        <v-dialog v-model="dialogSaveSongError" max-width="400" :lazy="true" persistent>
            <v-card>
                <v-card-title class="headline">{{ $t('save-song-error-title') }}</v-card-title>

                <v-card-text>
                    <p class="text-xs-left">
                        {{ $t('save-song-error-title') }}
                    </p>
                </v-card-text>
                
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn flat @click.native="dialogSaveSongError = false">{{ $t('close') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
// @ is an alias to /src
import { mapGetters, mapActions } from 'vuex';
import store from '@/stores';
import * as types from '@/stores/types';
import SongModel from '@/models/Song';

export default {
    name:     'song-editor',
    store:    store,
    data:     function()
    {
        return {
            dialogSaveSongError: false,
        };
    },
    computed: {
        ...mapGetters({
            song:           'song',
            editionMode:    'editionMode',
            isSongModified: 'isSongModified',
        }),
        keys: function()
        {
            return SongModel.getKeysList();
        },
        timeSignaturesList: function()
        {
            const list = [...SongModel.getTimeSignaturesList()];

            list.unshift('');

            return list;
        },
        capos: function()
        {
            return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 13, 14, 15, 16];
        },
        title:        {
            get: function()
            {
                return this.song.title;
            },
            set: function(value)
            {
                this.$store.commit(types.M_SONG_UPDATE, { property: 'title', value });
            },
        },
        author: {
            get: function()
            {
                return this.song.author;
            },
            set: function(value)
            {
                this.$store.commit(types.M_SONG_UPDATE, { property: 'author', value });
            },
        },
        album: {
            get: function()
            {
                return this.song.album;
            },
            set: function(value)
            {
                this.$store.commit(types.M_SONG_UPDATE, { property: 'album', value });
            },
        },
        key: {
            get: function()
            {
                return this.song.key;
            },
            set: function(value)
            {
                if (value === undefined)
                {
                    value = '';
                }
                
                this.$store.commit(types.M_SONG_UPDATE, { property: 'key', value });
            },
        },
        timeSignature: {
            get: function()
            {
                return this.song.timeSignature;
            },
            set: function(value)
            {
                if (value === undefined)
                {
                    value = '';
                }
                
                this.$store.commit(types.M_SONG_UPDATE, { property: 'timeSignature', value });
            },
        },
        capo: {
            get: function()
            {
                return this.song.capo;
            },
            set: function(value)
            {
                this.$store.commit(types.M_SONG_UPDATE, { property: 'capo', value });
            },
        },
        tempo: {
            get: function()
            {
                return this.song.tempo;
            },
            set: function(value)
            {
                this.$store.commit(types.M_SONG_UPDATE, { property: 'tempo', value });
            },
        },
        displayMeasureNumber: {
            get: function()
            {
                return this.song.displayMeasureNumber;
            },
            set: function(value)
            {
                this.$store.commit(types.M_SONG_UPDATE, { property: 'displayMeasureNumber', value });
            },
        },
        canBeSaved: function()
        {
            return (!this.isSongModified || !SongModel.canBeSaved(this.song));
        },
    },
    methods: {
        ...mapActions({
            updateSongAction: types.A_SONG_UPDATE,
            addPartAction:    types.A_SONG_ADD_PART,
            saveSongAction:   types.A_SONGSLIST_ADD_SONG,
        }),
        /**
         * Enregistrement d'une chanson
         *
         * @author Fabien Bellanger
         */
        saveSong: function()
        {
            if (SongModel.canBeSaved(this.song))
            {
                this.saveSongAction(this.song);
            }
            else
            {
                this.dialogSaveSongError = true;
            }
        },
    },
};
</script>
